import { computed, makeAutoObservable, runInAction } from 'mobx';

import { client } from 'contexts/ApolloClient/apolloClient';
import { VALIDATE_AND_OVERLAPS_BY_SEQUENCE_GROUP } from 'pages/WorkAssignments/gql/eligibilityValidation';

import { assignJobReturnSidebarText } from './text';

class AssignJobReturnSidebarStore {
	selectedAssignmentId = null;
	selectedReturnJob = null;
	showSelectedReturnJob = false;
	selectedDriver = null;
	reasons = [];
	overlaps = [];
	overlappingAction = '';
	delayMinutes = null;
	minDelayMinutes = null;
	maxDelayMinutes = null;
	cannotCancelOverlaps = false;
	showsReasons = false;
	assigningDriverId = null;
	bulkValidationMap = {};
	_bulkAbortController = null;

	constructor() {
		makeAutoObservable(
			this,
			{
				isDelayOptionAvailable: computed,
				allowToDeleteOverlaps: computed,
				allowToDelayOverlaps: computed,
				delayError: computed,
				isAssignmentDisabled: computed,
			},
			{ autoBind: true }
		);
	}

	get isDelayOptionAvailable() {
		return this.minDelayMinutes != null;
	}

	get allowToDeleteOverlaps() {
		return this.overlappingAction === 'delete';
	}

	get allowToDelayOverlaps() {
		return this.overlappingAction === 'delay';
	}

	get delayError() {
		if (!this.allowToDelayOverlaps || this.delayMinutes == null) return null;
		if (this.minDelayMinutes != null && this.delayMinutes < this.minDelayMinutes)
			return assignJobReturnSidebarText.delayTooSmall;
		if (this.maxDelayMinutes != null && this.delayMinutes > this.maxDelayMinutes)
			return assignJobReturnSidebarText.delayTooLarge;
		return null;
	}

	get isAssignmentDisabled() {
		return (
			(this.overlaps.length > 0 && !this.allowToDeleteOverlaps && !this.allowToDelayOverlaps) ||
			this.cannotCancelOverlaps ||
			!!this.delayError
		);
	}

	setOverlappingAction(value) {
		this.overlappingAction = value;
	}

	setDelayMinutes(value) {
		this.delayMinutes = value;
	}

	reset() {
		this.cancelBulkValidation();
		this.selectedAssignmentId = null;
		this.selectedReturnJob = null;
		this.showSelectedReturnJob = false;
		this.selectedDriver = null;
		this.reasons = [];
		this.overlaps = [];
		this.overlappingAction = '';
		this.delayMinutes = null;
		this.minDelayMinutes = null;
		this.maxDelayMinutes = null;
		this.cannotCancelOverlaps = false;
		this.showsReasons = false;
		this.assigningDriverId = null;
		this.bulkValidationMap = {};
	}

	async validateDrivers(drivers, variables) {
		if (this._bulkAbortController) {
			this._bulkAbortController.abort();
		}

		const controller = new AbortController();
		this._bulkAbortController = controller;
		this.bulkValidationMap = {};

		for (const driver of drivers) {
			if (controller.signal.aborted) return;
			const driverId = driver?.crewMember?.id;
			if (!driverId) continue;

			try {
				const { data } = await client.query({
					query: VALIDATE_AND_OVERLAPS_BY_SEQUENCE_GROUP,
					fetchPolicy: 'no-cache',
					variables: {
						crewMemberId: driverId,
						workAssignmentSequenceGroupId: variables.workAssignmentSequenceGroupId,
						fromStationId: variables.fromStationId,
						toStationId: variables.toStationId,
						activityType: variables.activityType,
					},
					context: { fetchOptions: { signal: controller.signal } },
				});

				if (controller.signal.aborted) return;

				const reasons = data?.eligibility ?? [];
				const overlapsResponse = data?.overlaps ?? {};
				const overlaps = overlapsResponse.overlappingActivities ?? [];

				runInAction(() => {
					this.bulkValidationMap[driverId] = {
						reasons,
						overlaps,
						showReasons: reasons.length > 0 || overlaps.length > 0,
					};
				});
			} catch {
				if (controller.signal.aborted) return;
				runInAction(() => {
					this.bulkValidationMap[driverId] = {
						reasons: [],
						overlaps: [],
						showReasons: false,
						error: true,
					};
				});
			}
		}
	}

	cancelBulkValidation() {
		if (this._bulkAbortController) {
			this._bulkAbortController.abort();
			this._bulkAbortController = null;
		}
	}

	openReturnJobSidebar(assignmentKey, { showSelectedReturnJob = false } = {}) {
		this.selectedAssignmentId = assignmentKey;
		this.showSelectedReturnJob = showSelectedReturnJob;
	}

	closeReturnJobSidebar() {
		this.selectedAssignmentId = null;
		this.showSelectedReturnJob = false;
	}

	selectReturnJob(assignmentKey, returnJob) {
		this.selectedAssignmentId = assignmentKey;
		this.selectedReturnJob = {
			assignmentKey,
			...returnJob,
		};
		this.showSelectedReturnJob = false;
	}

	clearSelectedReturnJob() {
		this.selectedReturnJob = null;
		this.showSelectedReturnJob = false;
	}

	isSelectedDriver(assignmentKey) {
		return this.selectedReturnJob?.assignmentKey === assignmentKey;
	}

	startAssigning(driver) {
		this.selectedDriver = driver;
		this.assigningDriverId = driver?.crewMember?.id ?? null;
	}

	stopAssigning() {
		this.assigningDriverId = null;
	}

	showValidationIssues({
		driver,
		reasons = [],
		overlaps = [],
		minDelayMinutes = null,
		maxDelayMinutes = null,
		cannotCancelOverlaps = false,
	}) {
		this.selectedDriver = driver ?? this.selectedDriver;
		this.reasons = reasons;
		this.overlaps = overlaps;
		this.cannotCancelOverlaps = cannotCancelOverlaps;
		this.overlappingAction = '';
		this.minDelayMinutes = minDelayMinutes;
		this.maxDelayMinutes = maxDelayMinutes;
		this.delayMinutes = minDelayMinutes;
		this.showsReasons = reasons.length > 0 || overlaps.length > 0;
	}

	hideReasons() {
		this.showsReasons = false;
		this.reasons = [];
		this.overlaps = [];
		this.overlappingAction = '';
		this.delayMinutes = null;
		this.minDelayMinutes = null;
		this.maxDelayMinutes = null;
		this.cannotCancelOverlaps = false;
	}
}

export const assignJobReturnSidebarStore = new AssignJobReturnSidebarStore();
